import React, { useState, useEffect } from 'react';
import { Smile, Frown, Meh, Brain, Loader2 } from 'lucide-react';
import { advancedReportsAPI } from '../../services/api';
import { Alert, AlertDescription } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { cn } from '../../lib/utils';

interface SentimentAnalysisProps {
  dateRange: {
    startDate: Date;
    endDate: Date;
  };
  selectedAgent: number | 'all';
  selectedChannel: number | 'all';
}

interface SentimentData {
  overall_sentiment_score: number;
  sentiment_distribution: {
    positive: number;
    negative: number;
    neutral: number;
  };
  sentiment_trend: Array<{
    date: string;
    sentiment_score: number;
    message_count: number;
  }>;
  top_positive_messages: Array<{
    content: string;
    score: number;
    date: string;
    conversation_id: number;
  }>;
  top_negative_messages: Array<{
    content: string;
    score: number;
    date: string;
    conversation_id: number;
  }>;
  sentiment_by_channel: Record<string, { avg_sentiment: number; message_count: number }>;
  sentiment_by_agent: Record<string, { avg_sentiment: number; message_count: number }>;
  keyword_analysis: {
    positive_keywords: string[];
    negative_keywords: string[];
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getSentimentLabel = (score: number) => {
  if (score > 0.1) return 'Positive';
  if (score < -0.1) return 'Negative';
  return 'Neutral';
};

const getSentimentColor = (score: number) => {
  if (score > 0.1)  return 'text-emerald-600 dark:text-emerald-400';
  if (score < -0.1) return 'text-red-500 dark:text-red-400';
  return 'text-amber-500 dark:text-amber-400';
};

const getSentimentIcon = (score: number) => {
  if (score > 0.1)  return <Smile  className="h-4 w-4" />;
  if (score < -0.1) return <Frown  className="h-4 w-4" />;
  return               <Meh    className="h-4 w-4" />;
};

// Score [-1, 1] → progress [0, 100]
const toProgressValue = (score: number) => ((score + 1) / 2) * 100;

// ─── Mini score row used in channel/agent tables ──────────────────────────────
const ScoreRow: React.FC<{ label: string; subtitle: string; score: number }> = ({
  label,
  subtitle,
  score,
}) => (
  <div className="flex items-center gap-3 py-2">
    <div className="min-w-0 flex-1">
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm truncate">{label}</span>
        <span className={cn('text-xs font-medium tabular-nums ml-2 shrink-0', getSentimentColor(score))}>
          {score.toFixed(2)}
        </span>
      </div>
      <Progress value={toProgressValue(score)} className="h-1.5" />
    </div>
    <span className="text-xs text-muted-foreground shrink-0 w-20 text-right">{subtitle}</span>
  </div>
);

// ─── Component ───────────────────────────────────────────────────────────────

export const SentimentAnalysis: React.FC<SentimentAnalysisProps> = ({
  dateRange,
  selectedAgent,
  selectedChannel,
}) => {
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState<string | null>(null);
  const [sentimentData, setSentimentData] = useState<SentimentData | null>(null);

  useEffect(() => {
    fetchSentimentData();
  }, [dateRange, selectedAgent, selectedChannel]);

  const fetchSentimentData = async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        start_date: dateRange.startDate.toISOString().split('T')[0],
        end_date:   dateRange.endDate.toISOString().split('T')[0],
      };
      if (selectedAgent !== 'all')   params.agent_id   = selectedAgent;
      if (selectedChannel !== 'all') params.channel_id = selectedChannel;
      const data = await advancedReportsAPI.getSentimentAnalysis(params);
      setSentimentData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch sentiment analysis');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="destructive" className="m-2">
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );
  }

  if (!sentimentData) {
    return (
      <Alert className="m-2">
        <AlertDescription>No sentiment data available for the selected period.</AlertDescription>
      </Alert>
    );
  }

  const { overall_sentiment_score: score, sentiment_distribution: dist } = sentimentData;

  const totalMessages =
    dist.positive + dist.negative + dist.neutral;

  const positivePct = totalMessages > 0 ? (dist.positive / totalMessages) * 100 : 0;
  const neutralPct  = totalMessages > 0 ? (dist.neutral  / totalMessages) * 100 : 0;
  const negativePct = totalMessages > 0 ? (dist.negative / totalMessages) * 100 : 0;

  return (
    <div className="space-y-4">

      {/* ── Section 1: Overall score + distribution strip ────────────────── */}
      <div className="flex flex-wrap divide-x divide-border rounded-lg border border-border/60 overflow-hidden">

        {/* Overall score */}
        <div className="flex flex-col justify-between px-5 py-4 min-w-[180px]">
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
            <Brain className="h-3.5 w-3.5" />
            Overall Sentiment
          </div>
          <div className="flex items-end gap-3">
            <span className={cn('text-3xl font-bold tabular-nums leading-none', getSentimentColor(score))}>
              {score.toFixed(2)}
            </span>
            <div className={cn('flex items-center gap-1 text-sm font-medium mb-0.5', getSentimentColor(score))}>
              {getSentimentIcon(score)}
              {getSentimentLabel(score)}
            </div>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {totalMessages.toLocaleString()} messages analysed
          </p>
        </div>

        {/* Segmented colour bar + per-bucket counts */}
        <div className="flex flex-1 flex-col justify-between px-5 py-4 min-w-[260px]">
          <div className="text-xs text-muted-foreground mb-3">Distribution</div>
          {/* Stacked bar */}
          <div className="flex h-2.5 w-full overflow-hidden rounded-full bg-muted">
            <div className="bg-emerald-500 transition-all" style={{ width: `${positivePct}%` }} />
            <div className="bg-amber-400   transition-all" style={{ width: `${neutralPct}%`  }} />
            <div className="bg-red-500     transition-all" style={{ width: `${negativePct}%` }} />
          </div>
          {/* Legend */}
          <div className="flex items-center gap-5 mt-3">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
              <span className="text-xs text-muted-foreground">Positive</span>
              <span className="text-xs font-semibold tabular-nums text-foreground ml-1">
                {dist.positive.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">({positivePct.toFixed(0)}%)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-amber-400 shrink-0" />
              <span className="text-xs text-muted-foreground">Neutral</span>
              <span className="text-xs font-semibold tabular-nums text-foreground ml-1">
                {dist.neutral.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">({neutralPct.toFixed(0)}%)</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-red-500 shrink-0" />
              <span className="text-xs text-muted-foreground">Negative</span>
              <span className="text-xs font-semibold tabular-nums text-foreground ml-1">
                {dist.negative.toLocaleString()}
              </span>
              <span className="text-xs text-muted-foreground">({negativePct.toFixed(0)}%)</span>
            </div>
          </div>
        </div>

      </div>

      {/* ── Section 2: By Channel + By Agent — 2-col bordered panels ────── */}
      <div className="grid gap-4 md:grid-cols-2">

        {/* By Channel */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <span className="text-sm font-medium">Sentiment by Channel</span>
          </div>
          <div className="divide-y divide-border/40 px-4">
            {Object.entries(sentimentData.sentiment_by_channel).length > 0
              ? Object.entries(sentimentData.sentiment_by_channel).map(([channel, data]) => (
                  <ScoreRow
                    key={channel}
                    label={channel}
                    subtitle={`${data.message_count} msgs`}
                    score={data.avg_sentiment}
                  />
                ))
              : (
                <p className="py-8 text-center text-sm text-muted-foreground">No channel data</p>
              )}
          </div>
        </div>

        {/* By Agent */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <span className="text-sm font-medium">Sentiment by Agent</span>
            <span className="ml-auto text-xs text-muted-foreground">Top 5</span>
          </div>
          <div className="divide-y divide-border/40 px-4">
            {Object.entries(sentimentData.sentiment_by_agent).length > 0
              ? Object.entries(sentimentData.sentiment_by_agent)
                  .slice(0, 5)
                  .map(([agent, data]) => (
                    <ScoreRow
                      key={agent}
                      label={agent}
                      subtitle={`${data.message_count} msgs`}
                      score={data.avg_sentiment}
                    />
                  ))
              : (
                <p className="py-8 text-center text-sm text-muted-foreground">No agent data</p>
              )}
          </div>
        </div>

      </div>

      {/* ── Section 3: Keywords — one row, two columns of chips ──────────── */}
      <div className="grid gap-4 md:grid-cols-2">

        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <Smile className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-sm font-medium">Positive Keywords</span>
          </div>
          <div className="flex flex-wrap gap-1.5 px-4 py-3">
            {sentimentData.keyword_analysis.positive_keywords.length > 0
              ? sentimentData.keyword_analysis.positive_keywords.map((kw, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300 text-xs"
                  >
                    {kw}
                  </Badge>
                ))
              : <span className="text-sm text-muted-foreground py-1">No keywords available</span>}
          </div>
        </div>

        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <Frown className="h-3.5 w-3.5 text-red-500" />
            <span className="text-sm font-medium">Negative Keywords</span>
          </div>
          <div className="flex flex-wrap gap-1.5 px-4 py-3">
            {sentimentData.keyword_analysis.negative_keywords.length > 0
              ? sentimentData.keyword_analysis.negative_keywords.map((kw, i) => (
                  <Badge
                    key={i}
                    variant="outline"
                    className="border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300 text-xs"
                  >
                    {kw}
                  </Badge>
                ))
              : <span className="text-sm text-muted-foreground py-1">No keywords available</span>}
          </div>
        </div>

      </div>

      {/* ── Section 4: Sample messages — compact score-tagged list ───────── */}
      <div className="grid gap-4 md:grid-cols-2">

        {/* Positive */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <Smile className="h-3.5 w-3.5 text-emerald-500" />
            <span className="text-sm font-medium">Top Positive Messages</span>
          </div>
          <div className="divide-y divide-border/40 max-h-72 overflow-y-auto">
            {sentimentData.top_positive_messages.length > 0
              ? sentimentData.top_positive_messages.map((msg, i) => (
                  <div key={i} className="flex gap-3 px-4 py-3">
                    {/* Score pill */}
                    <span className="shrink-0 mt-0.5 text-xs font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-950 border border-emerald-200 dark:border-emerald-800 rounded px-1.5 py-0.5 h-fit">
                      +{msg.score.toFixed(2)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground leading-snug line-clamp-2">
                        "{msg.content}"
                      </p>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {new Date(msg.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              : <p className="py-8 text-center text-sm text-muted-foreground">No messages available</p>}
          </div>
        </div>

        {/* Negative */}
        <div className="rounded-lg border border-border/60 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border/60 bg-muted/20">
            <Frown className="h-3.5 w-3.5 text-red-500" />
            <span className="text-sm font-medium">Top Negative Messages</span>
          </div>
          <div className="divide-y divide-border/40 max-h-72 overflow-y-auto">
            {sentimentData.top_negative_messages.length > 0
              ? sentimentData.top_negative_messages.map((msg, i) => (
                  <div key={i} className="flex gap-3 px-4 py-3">
                    {/* Score pill */}
                    <span className="shrink-0 mt-0.5 text-xs font-semibold tabular-nums text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded px-1.5 py-0.5 h-fit">
                      {msg.score.toFixed(2)}
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm text-foreground leading-snug line-clamp-2">
                        "{msg.content}"
                      </p>
                      <span className="text-xs text-muted-foreground mt-1 block">
                        {new Date(msg.date).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))
              : <p className="py-8 text-center text-sm text-muted-foreground">No messages available</p>}
          </div>
        </div>

      </div>
    </div>
  );
};
